/* jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import Content from 'app/components/common/Content.jsx';
import StyledSelect from 'app/components/common/StyledSelect.jsx';
import FormUtilityMixin from 'app/mixins/FormUtilityMixin.js';
import ErrorModule from 'app/mixins/ErrorModule.jsx';
import FieldError from 'app/components/common/FieldError.jsx';
import ConstantMixin from 'app/mixins/ConstantMixin.js';
import * as IconFactory from 'app/components/common/IconFactory.jsx';
import DeleteModal from 'app/components/common/DeleteModal.jsx';
import ConfirmModal from 'app/components/common/ConfirmModal.jsx';
import * as _ from 'underscore';
import SocialLoginProvider from 'app/redux/Providers/SocialLoginProvider.jsx';
import Loading from 'app/components/common/Loading.jsx';
import * as appConstants from 'app/redux/constants.js';
import UserMessages from 'app/modules/UserMessageModule.js';
import axios from 'axios';
import ComponentWithContext from 'app/components/Routers/ComponentWithContext.jsx';
import { compose } from 'redux';
import AccountPage from '@/shadcn/components/AccountPage';
import ClientAccountPage from '@/shadcn/components/ClientAccountPage';
import makeToast from 'app/modules/makeToast.js';

/*
 * The unpause dialog's choice of what to post first. The dialog is built once and handed to the
 * modal stack, so a value passed into it never changes and the select snapped back to it; the
 * choice is held here so the select shows what was picked. onChange still writes it to the user.
 */
function FirstPullChoice({ options, initial, onChange }) {
	const [value, setValue] = React.useState(initial);

	return (
		<StyledSelect
			options={options}
			linkedField="user.firstPullFlag"
			value={value}
			selectClass="large pause-first-pull"
			onChange={(e) => {
				setValue(e.target.value);
				onChange(e);
			}}/>
	);
}

class Account extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			user: {},
			errors: null,
			resets: 0,
			isEditing: {
				email: false,
				password: false,
			},
			timezones: [],
			dropzone: null,
			credID: '',
			username: '',
			showSocialOptions: false,
			passwordStrength: 0,
			passwordConfirmation: '',
			currentModal: null,
			isAddingLogin: false,
			redirectOnPasswordUpdate: false,
		};

		this.deleteAccount = this.deleteAccount.bind(this);
		this._updateEditingState = this._updateEditingState.bind(this);
		this._resetEditingState = this._resetEditingState.bind(this);
		this.onBeginEdit = this.onBeginEdit.bind(this);
		this.onCancelEdit = this.onCancelEdit.bind(this);
		this.onCancelSocialLoginUpdate = this.onCancelSocialLoginUpdate.bind(this);
		this.onUpdateField = this.onUpdateField.bind(this);
		this.onTimezoneUpdate = this.onTimezoneUpdate.bind(this);
		this.onMailOptionsUpdate = this.onMailOptionsUpdate.bind(this);
		this.onActivityCadenceUpdate = this.onActivityCadenceUpdate.bind(this);
		this.updateUser = this.updateUser.bind(this);
		this.updatePassword = this.updatePassword.bind(this);
		this.updatePasswordValue = this.updatePasswordValue.bind(this);
		this.addSocialLogin = this.addSocialLogin.bind(this);
		this.saveSocialLogin = this.saveSocialLogin.bind(this);
		this.removeSocialLogin = this.removeSocialLogin.bind(this);
		this.resendEmailConfirmation = this.resendEmailConfirmation.bind(this);
		this.uploadAvatar = this.uploadAvatar.bind(this);
		this.resetAvatar = this.resetAvatar.bind(this);
		this.generateApiKey = this.generateApiKey.bind(this);
		this.togglePause = this.togglePause.bind(this);
		this.convertClient = this.convertClient.bind(this);

		this.debouncedUpdater = _.debounce(this.onUpdateField, 1000);

		props.getSocialLogins();
		props.getTimezones();

	}

	static getDerivedStateFromProps(props, state) {
		let nextState = {},
			apiErrors = null,
			appErrors = null;

		// a copy: the handlers edit it in place, and redux has to keep what was last saved
		if (props.app.userLoaded === true && _.isEmpty(state.user)) {
			nextState.user = _.clone(props.user.detail);
		}

		apiErrors = props.getApiErrors([
			'USER_UPDATE',
			'USER_GENERATE_API_KEY',
			'USER_DELETE',
			'SOCIAL_LOGIN_CREATE',
			'SOCIAL_LOGIN_DELETE',
			'USER_DETACH_CLIENT'
		]);
		if (apiErrors.length) {
			nextState.errors = apiErrors;
		}

		appErrors = props.getAppErrors([
			'USER_UPDATE',
			'USER_GENERATE_API_KEY',
			'USER_DELETE',
			'SOCIAL_LOGIN_CREATE',
			'SOCIAL_LOGIN_DELETE'
		]);
		if (appErrors.length) {
			nextState.errors = appErrors;
		}

		if (!apiErrors.length && !appErrors.length) {
			nextState.errors = null;
		}

		return (Object.keys(nextState).length) ? nextState : null;

	}

	componentDidMount() {
		this.props.authReturnHandler.apply(this, [{
			path: '/account'
		}, (credID, username) => {
			this.props.resetAuthState();
			this.setState({
				credID,
				username
			}, () => {
				/*
					Not while the constructor's list fetch is still out: both answers
					replace the whole list, so if the list landed second its copy,
					read before the new login existed, wiped it from the page. Once
					the list is in, componentDidUpdate saves it instead.
				*/
				if (this.props.app.loading.SOCIAL_LOGIN_LIST !== true) {
					this.saveSocialLogin();
				}
			});
		}]);

		if (this.props.router.location.hash.match("password")) {
			let isEditing = _.clone(this.state.isEditing),
				redirectOnPasswordUpdate = this.state.redirectOnPasswordUpdate;

			isEditing.password = true;
			if (this.props.user.detail.isClient !== undefined && this.props.user.detail.isClient === true) {
				redirectOnPasswordUpdate = true;
			}

			this.setState({
				isEditing,
				redirectOnPasswordUpdate
			});

		}

	}

	componentDidUpdate(prevProps, prevState) {
		let errors = [];

		if (prevProps.app.loading.USER_FETCH && this.props.app.loading.USER_FETCH === false) {
			let user = _.clone(this.props.user.detail);

			if (user.timezone === '') {
				user.timezone = 'America/Los_Angeles';
			}

			this.setState({
				user
			});
		}

		// sign out only once the account is gone; a delete that failed stays here and says so
		if (prevProps.app.loading.USER_DELETE === true && this.props.app.loading.USER_DELETE === false) {
			if (this.props.getApiErrors(['USER_DELETE']).length) {
				makeToast("", "error", {
					title: "We couldn't delete your account",
					description: "Please try again."
				});
			} else {
				window.location.replace(this.props.app.config.loginUrl);
			}
		}

		if (prevProps.app.loading.SOCIAL_LOGIN_LIST === true && this.props.app.loading.SOCIAL_LOGIN_LIST === false && this.state.credID !== '') {
			this.saveSocialLogin();
		}

		if (prevProps.app.loading.SOCIAL_LOGIN_CREATE === true && this.props.app.loading.SOCIAL_LOGIN_CREATE === false) {
			this.setState({
				credID: ''
			});
			this._resetEditingState();
		}

		if (!_.isEqual(prevProps.user.detail, this.props.user.detail)) {
			this.setState({
				user: _.clone(this.props.user.detail)
			});
			if (this.state.redirectOnPasswordUpdate && this.props.user.detail.needsPassword === false) {
				this.props.router.navigate("/socials");
				return;
			}
			errors = this.props.getApiErrors(['USER_UPDATE']);
			if (errors.length === 0) {
				this._resetEditingState();
			}
		}

		if (prevProps.app.loading.USER_UPDATE === true && this.props.app.loading.USER_UPDATE === false) {
			errors = this.props.getApiErrors(['USER_UPDATE']);
			if (errors.length === 0) {
				this._resetEditingState();
				if (this.state.user.resendEmailConf !== undefined) {
					makeToast("Confirmation email resent", "success");
					let nextUser = Object.assign({}, this.state.user);
					delete nextUser.resendEmailConf;
					this.setState({
						user: nextUser
					});
				} else {
					makeToast("Account updated", "success");
				}
			} else if (!this.isFieldError(errors[0])) {
				this.revertUnsavedSettings(errors[0]);
			}
		}

		if (prevProps.app.loading.USER_DETACH_CLIENT === true && this.props.app.loading.USER_DETACH_CLIENT === false) {
			if (this.state.errors === null) {
				window.location.reload(true);
			}
		}

		if (prevProps.app.loading.USER_GENERATE_API_KEY === true && this.props.app.loading.USER_GENERATE_API_KEY === false) {
			errors = this.props.getApiErrors(['USER_GENERATE_API_KEY']);
			if (errors.length) {
				if (errors[0].errors.perms !== undefined) {
					this.props.showUpgradeModal(this.props.closeAllModals, null, errors, 'apikey');
					this.props.deleteApiError(['USER_GENERATE_API_KEY']);
				}
			} else {
				makeToast("API key updated", "success");
			}
		}


	}

	/** Email and password failures are shown under their fields, in the editor that sent them. */
	isFieldError(failure) {

		let validation = failure?.errors?.validation;

		return _.isObject(validation) && ['email', 'password'].some((field) => validation[field] !== undefined);

	}

	/*
	 * An autosaved setting didn't save: the organization, timezone, email options or posting
	 * pause. Each control goes back to what's stored, so none of them shows a value that didn't
	 * stick, and the toast says so. A field with its editor still open keeps what was typed.
	 */
	revertUnsavedSettings(failure) {

		let saved = _.clone(this.props.user.detail),
			validation = failure?.errors?.validation,
			reason = _.isObject(validation) ? _.find(_.values(validation), _.isString) : null;

		Object.keys(this.state.isEditing).forEach((section) => {
			if (this.state.isEditing[section] && this.state.user[section] !== undefined) {
				saved[section] = this.state.user[section];
			}
		});

		this.setState({
			user: saved,
			// remounts the organization input, which is uncontrolled so typing isn't held up by the debounce
			resets: this.state.resets + 1
		});

		makeToast("", "error", {
			title: "We couldn't save that change",
			description: reason || "Please try again."
		});

	}

	_updateEditingState(key, state) {

		let isEditing = Object.assign({}, this.state.isEditing);

		Object.keys(isEditing).map((editKey) => {
			if (key === editKey) {
				isEditing[editKey] = state;
			} else {
				isEditing[editKey] = false;
				this.props.deleteAppError(['USER_UPDATE']);
				this.props.deleteApiError(['USER_UPDATE']);
			}
		});

		this.setState({
			isEditing: isEditing
		});

	}

	_resetEditingState() {

		let isEditing = this.state.isEditing;

		for (let key in isEditing) {
			isEditing[key] = false;
		}

		this.props.deleteAppError([appConstants.USER_UPDATE]);

		this.setState({
			isEditing: isEditing
		});

	}

	deleteAccount() {

		let confirmAction = (e) => {
				e.preventDefault();
				this.props.deleteUser(this.state.user.id);
				this.props.closeAllModals(null);
			},
			cancelAction = (e) => {
				this.props.closeAllModals(null);
			},
			modal = (
				<DeleteModal
					modalCopy={"Are you sure you want to delete your account?"}
					cancelAction={cancelAction}
					confirmAction={confirmAction} />
			);

		this.props.addModal(modal);

	}

	onBeginEdit(section) {

		this._updateEditingState(section, true);

	}

	onCancelEdit(section) {

		let user = Object.assign({}, this.state.user);

		this._updateEditingState(section, false);
		this.props.deleteAppError(['USER_UPDATE']);
		this.props.deleteApiError(['USER_UPDATE']);

		// reset the value
		user[section] = this.props.user.detail[section];

		if (section === 'password') {
			user.password = '';
		}

		this.setState({
			user
		});

	}

	onCancelSocialLoginUpdate(e) {

		this._resetEditingState();
		this.setState({
			credID: ''
		});

	}

	onUpdateField(e) {

		let field = e.target.dataset?.field,
			value = e.target.value,
			immediate = e.target.dataset?.immediate,
			user = this.state.user;

		user[field] = value;

		this.setState({
			user
		});

		// string value sent through dataset
		if (immediate === "true") {
			this.updateUser();
		}

	}

	onTimezoneUpdate(timezone) {

		let user = Object.assign({}, this.state.user);

		user.timezone = timezone;

		this.setState({
			user
		}, () => {
			// avoid the race condition, wait until the state is updated!
			this.updateUser();
		});



	}

	onMailOptionsUpdate(field, newState) {
		let constants = this.props.app.config.constants,
			value = 0,
			user = this.state.user;

		switch (field) {
			case 'info':
				value = constants.MAIL_INFO;
				break;

			case 'alert':
				value = constants.MAIL_ALERT;
				break;

			case 'stats':
				value = constants.MAIL_STATS;
				break;

		}

		if (newState) {
			user.mailOpts = parseInt(user.mailOpts, 10) | value;
		} else {
			user.mailOpts = parseInt(user.mailOpts, 10) - value;
		}

		this.setState({
			user: user
		}, () => {
			this.updateUser();
		});

	}

	onActivityCadenceUpdate(recap) {
		let constants = this.props.app.config.constants,
			user = Object.assign({}, this.state.user),
			opts = parseInt(user.mailOpts, 10);

		// DAILY and WEEKLY are mutually exclusive: DAILY-bit users already
		// receive the weekly recap on the weekly day, so setting both is
		// redundant. Clear both, then set at most one.
		opts &= ~constants.MAIL_ACTIVITY_DAILY;
		opts &= ~constants.MAIL_ACTIVITY_WEEKLY;

		if (recap === 'daily') {
			opts |= constants.MAIL_ACTIVITY_DAILY;
		} else if (recap === 'weekly') {
			opts |= constants.MAIL_ACTIVITY_WEEKLY;
		}

		user.mailOpts = opts;

		this.setState({
			user: user
		}, () => {
			this.updateUser();
		});

	}

	updateUser(e) {

		let	updatedUser = _.clone(this.state.user);

		// if cred id is set, send it to create/replace a social login
		if (this.state.credID !== '') {
			updatedUser.credID = this.state.credID;
		}

		// one-off instructions for this save rather than settings, so a later save mustn't repeat them
		if (updatedUser.resendEmailConf === 1 || updatedUser.resetAvatar === 1) {
			let user = this.state.user;
			user.resendEmailConf = 0;
			user.resetAvatar = 0;
			this.setState({
				user
			});
		}

		this.props.updateUser(updatedUser);

	}

	updatePassword(e) {

		let user = this.state.user,
			errors = {};

		this.props.deleteAppError([appConstants.USER_UPDATE]);

		if (!user.password || user.password === '' || user.password.length < 8) {
			// mirror regular validation errors so we can use the mixin
			errors.errors = {};
			errors.errors.validation = {};
			errors.errors.validation['password'] = [];
			errors.errors.validation['password'].push('Invalid password, must be at least 8 characters long');
		} else if (user.password !== this.state.passwordConfirmation) {
			// mirror regular validation errors so we can use the mixin
			errors.errors = {};
			errors.errors.validation = {};
			errors.errors.validation['password-confirm'] = [];
			errors.errors.validation['password-confirm'].push("Password confirmation doesn't match");
		}

		if (Object.keys(errors).length > 0) {
			this.props.setAppError({
				type: appConstants.USER_UPDATE,
				error: errors
			});
		} else {
			this.updateUser();
		}

	}

	updatePasswordValue(e) {

		let user = Object.assign({}, this.state.user),
			passwordConfirmation = this.state.passwordConfirmation;

		if (e.target.id === "new-password") {
			user.password = e.target.value;
		} else if (e.target.id === "confirm-password") {
			passwordConfirmation = e.target.value;
		}

		this.setState({
			user,
			passwordConfirmation
		});

	}

	addSocialLogin(e) {

        let serviceId = e.currentTarget.dataset.serviceid,
			url = "/remote/beginloginupdate?lsID=:lsId&state=:state&final=:final",
			date = new Date(),
			state = ['LOGIN', serviceId, date.getTime()].join("_"),
			constants = this.props.app.config.constants;

        e.preventDefault();
        e.stopPropagation();

		if (this.state.isAddingLogin) {
			return;
		}

		this.setState({
			isAddingLogin: true
		}, () => {
			url = url.replace(':lsId', serviceId);
			url = url.replace(':state', state);
			let finalUrl = this.props.cleanUrl(window.location.href);
        	url = url.replace(':final', window.encodeURIComponent(finalUrl));
			window.location.assign(url);
		});

    }

	saveSocialLogin(e) {

		if (this.state.credID !== '' && typeof this.state.credID === "string") {
			let svcID = this.state.credID.split("_")[1];
			this.props.createSocialLogin({
				serviceID: svcID,
				credID: this.state.credID
			});
		}

	}

	removeSocialLogin(e) {

		let loginId = e.currentTarget.dataset.loginid;

		this.props.deleteSocialLogin(loginId);

	}

	resendEmailConfirmation(e) {

		let user = this.state.user;

		if (e) {
			e.preventDefault();
		}

		user.resendEmailConf = 1;

		this.setState({
			user
		});
		this.updateUser();

	}

	uploadAvatar(acceptedFiles, rejectedFiles) {
		let formData = new FormData();

        if (rejectedFiles.length) {
            let errorMsgs = [];
            rejectedFiles.map(({ file, errors }) => {
                let errs = [];
                errors.map((e) => {
                    errs.push(e.message);
                });
                errorMsgs.push(`${file.name}\n${errs.join("\n")}`);
            });

            if (errorMsgs.length) {
				this.avatarUploadFailed(errorMsgs.join(" "));
            }
        }

        if (acceptedFiles.length) {
            formData.append('type', 'avatar');

			acceptedFiles.map((file) => {
				formData.append('pubID', this.state.user.id);
                formData.append('file', file);
            });

            axios({
                url: '/upload.php',
                method: 'POST',
                data: formData,
                headers: { "Content-type": "multipart/form-data" },
                withCredentials: true,
                responseType: 'json'
            })
            .then((response) => {
                this.props.getUser();
				this._updateEditingState('avatar', false);
            })
            .catch((error) => {
                /*
                 * upload.php refuses an avatar with an empty body and the reason in the status
                 * line, whose text HTTP/2 drops, so the status code is what says what went wrong.
                 */
                let status = error.response?.status;

                this.avatarUploadFailed(
                    status === 413 ? "That image is over 3MB." :
                    status === 415 ? "Use a PNG, JPG or GIF image." :
                    "Please try again."
                );
            });
        }
	}

	avatarUploadFailed(reason) {

		makeToast("", "error", {
			title: "We couldn't update your avatar",
			description: reason
		});

	}

	resetAvatar() {

		let user = this.state.user;

		user.resetAvatar = 1;

		this.setState({
			user
		});
		this.updateUser();

	}

	togglePause() {
		let user = this.state.user,
			constants = this.props.app.config.constants,
			confirmPause = () => {
				let cancel = () => {
						this.props.closeAllModals();
					},
					confirm = () => {
						user.pauseButton = constants.PUB_PAUSE_USER;

						this.setState({
							user: user
						}, () => {
							this.props.updateUser(user);
							this.props.closeAllModals();
						});
					},
					modal = (
						<ConfirmModal
							modalCopy="Are you sure you want to pause your account and stop all posting?"
							cancelAction={cancel}
							confirmAction={confirm}/>
					);

				this.props.addModal(modal);
			},
			confirmUnpause = () => {
				let firstPullOptions = [
						{value: constants.FIRSTPULL_SINGLE, label: 'Post the most recent items now'},
						{value: constants.FIRSTPULL_OFF, label: 'Post when new items are published'},
						{value: constants.FIRSTPULL_ON, label: 'Post all items (up to max)'},
					],
					pauseBlock = (
						<div>
							<FirstPullChoice
								options={firstPullOptions}
								initial={this.state.user.firstPullFlag ?? constants.FIRSTPULL_SINGLE}
								onChange={FormUtilityMixin.requestChange.bind(this)}/>
						</div>
					),
					cancel = () => {
						this.props.closeAllModals();
					},
					confirm = () => {
						user.pauseButton = constants.PUB_PAUSE_OFF;
						// what the dialog shows is what's applied, including the default nobody touched
						user.firstPullFlag = parseInt(user.firstPullFlag ?? constants.FIRSTPULL_SINGLE, 10);

						this.setState({
							user: user
						}, () => {
							this.props.updateUser(user);
							this.props.closeAllModals();
						});
					},
					modal = (
						<ConfirmModal
							modalCopy={(
								<div className="confirm-unpause">
									<div className="">When should we make your next posts?</div>
									{pauseBlock}
								</div>
							)}
							cancelAction={cancel}
							cancelLabel="cancel"
							confirmAction={confirm}
							confirmLabel="Unpause posting"/>
					);

				this.props.addModal(modal);
			};

		if (parseInt(user.pauseButton, 10) === constants.PUB_PAUSE_OFF) {
			confirmPause();
		} else if (parseInt(user.pauseButton, 10) === constants.PUB_PAUSE_USER) {
			confirmUnpause();
		}

	}

	 generateApiKey() {

		this.props.getAPIKey(this.state.user.id);

	 }

	 convertClient(e) {

		 let confirmAction = (e) => {
 				e.preventDefault();
 				this.props.detachClient();
 				this.props.closeAllModals(null);
 			},
 			cancelAction = (e) => {
 				this.props.closeAllModals(null);
 			},
 			modal = (
 				<DeleteModal
 					modalCopy={"Are you sure you want to disconnect your account from " + this.props.user.detail.clientInfo.organization + "?"}
 					cancelAction={cancelAction}
					cancelLabel="Don't Disconnect"
 					confirmAction={confirmAction}
					confirmLabel="Disconnect" />
 			);

 		e.preventDefault();

 		this.props.addModal(modal);

	 }

	render() {
		let main = null;

		if (this.props.app.userLoaded === false) {
			return (
				<Loading/>
			);
		}

		// {(this.props.appName === "client") ? (
		// 	<fieldset>
		// 		<ErrorModule
		// 			app={this.props.app}
		// 			keys={['USER_DETACH_CLIENT']}
		// 			render={(state) => {
		// 				return (
		// 					<FieldError field="detach" errors={state.errors}/>
		// 				);
		// 			}}/>
		// 		<div className="alt">
		// 			<p>Your social media profiles are managed by {this.props.user.detail.clientInfo.organization}</p>
		// 			<button className="delete-button" onClick={this.convertClient}>
		// 				Disconnect from {this.props.user.detail.clientInfo.organization}
		// 			</button>
		// 		</div>
		// 	</fieldset>
		// ) : null }


		if (this.props.appName === "dlvr") {
			main = (
				<div className="tw-root">
					<AccountPage
						{...this.props}
						draft={this.state.user}
						resets={this.state.resets}
						errors={this.state.errors}
						isEditing={this.state.isEditing}
						onBeginEdit={this.onBeginEdit}
						onCancelEdit={this.onCancelEdit}
						debouncedUpdater={this.debouncedUpdater}
						onTimezoneUpdate={this.onTimezoneUpdate}
						onUpdateField={this.onUpdateField}
						updateUser={this.updateUser}
						resendEmailConfirmation={this.resendEmailConfirmation}
						updatePassword={this.updatePassword}
						updatePasswordValue={this.updatePasswordValue}
						onSaveAvatar={(file) => {
							this.uploadAvatar([file], []);
						}}
						resetAvatar={this.resetAvatar}
						togglePause={this.togglePause}
						generateApiKey={this.generateApiKey}
						deleteAccount={this.deleteAccount}
						onMailOptionsUpdate={this.onMailOptionsUpdate}
						onActivityCadenceUpdate={this.onActivityCadenceUpdate}
						addSocialLogin={this.addSocialLogin}
						removeSocialLogin={this.removeSocialLogin}/>
				</div>
			);
		} else {
			main = (
				<div className="tw-root">
					<ClientAccountPage
						{...this.props}
						draft={this.state.user}
						resets={this.state.resets}
						errors={this.state.errors}
						isEditing={this.state.isEditing}
						onBeginEdit={this.onBeginEdit}
						onCancelEdit={this.onCancelEdit}
						debouncedUpdater={this.debouncedUpdater}
						onTimezoneUpdate={this.onTimezoneUpdate}
						onUpdateField={this.onUpdateField}
						updateUser={this.updateUser}
						resendEmailConfirmation={this.resendEmailConfirmation}
						updatePassword={this.updatePassword}
						updatePasswordValue={this.updatePasswordValue}
						onSaveAvatar={(file) => {
							this.uploadAvatar([file], []);
						}}
						resetAvatar={this.resetAvatar}
						convertClient={this.convertClient}
						deleteAccount={this.deleteAccount}/>
				</div>
			);
		}

		return (
			<Content header={null} headerClass="white" main={main} modal={this.state.currentModal}/>
		);
	}
};

Account.displayName = "Account";

Account.propTypes = {
	context: PropTypes.string,
	appName: PropTypes.string
};

Account.defaultProps = {
	context: '',
	appName: "dlvr"
};

const enhance = compose(
	ComponentWithContext,
	SocialLoginProvider
);

export default enhance(Account);
